import { alpha, Theme } from '@mui/material';

export type PolicySeverity = 'restriction' | 'warning' | 'info';

export const SEVERITY_LABELS: Record<PolicySeverity, string> = {
  restriction: 'Restriction',
  warning: 'Warning',
  info: 'Guidance',
};

const TYPE_SEVERITY_MAP: Record<string, PolicySeverity> = {
  restriction: 'restriction',
  warning: 'warning',
  requirement: 'info',
  guideline: 'info',
};

/**
 * Severity says how firm a rule is, so it reads from `type` in every category,
 * escalation included. An escalation policy is authored as `requirement` and is
 * never a restriction — how soon it has to be answered is carried by its urgency
 * icon, not by this scale, and must not reach the category restriction badge.
 */
export const getSeverityForType = (type?: string | null): PolicySeverity =>
  TYPE_SEVERITY_MAP[type ?? ''] ?? 'info';

export interface SeverityColors {
  glyph: string;
  rail: string;
  /** Row background at rest. */
  fill: string;
  /** Row background when hovered or expanded. */
  fillActive: string;
}

export const getSeverityColors = (theme: Theme, severity: PolicySeverity): SeverityColors => {
  const { sys, extended } = theme.palette.custom.OV;
  // only a restriction is filled, so the few must-not-miss rules stand out
  const neutralFill = { fill: 'transparent', fillActive: sys.surfaceContainerHighest };
  // the dark container is a saturated red; at full strength it swamps the panel
  const isDark = theme.palette.mode === 'dark';

  switch (severity) {
    case 'restriction':
      return {
        glyph: sys.error,
        rail: sys.error,
        fill: alpha(sys.errorContainer, isDark ? 0.22 : 0.6),
        fillActive: isDark ? alpha(sys.errorContainer, 0.35) : sys.errorContainer,
      };
    case 'warning':
      return {
        glyph: extended.Signature.color,
        rail: extended.Signature.color,
        ...neutralFill,
      };
    default:
      return { glyph: sys.onSurfaceVariant, rail: sys.outlineVariant, ...neutralFill };
  }
};
